import { useState } from 'react';
import { Button } from '../core/Button';
import { useOfftimeDays } from '../../graphql/hooks/useUserDashboard';
import { ConfirmModal } from '../core/Modal';
import { Modal } from '../core/Modal';
import { MoreVertical } from 'lucide-react';
import { useCreateOfftimeDay, useUpdateOfftimeDay, useDeleteOfftimeDay } from '../../graphql/hooks/useAdminDashboard';

export function HolidaysView() {
  const [holidayItemsPerPage, setHolidayItemsPerPage] = useState(10);
  const [holidayCurrentPage, setHolidayCurrentPage] = useState(1);
  const [isAddHolidayModalOpen, setIsAddHolidayModalOpen] = useState(false);

  const { data: offtimeDays } = useOfftimeDays(holidayItemsPerPage, holidayCurrentPage);

  const holidaysPaginator = offtimeDays?.getOfftimeDays?.paginatorInfo;
  const holidaysData = offtimeDays?.getOfftimeDays?.data ?? [];

  const [openHolidayDropdownId, setOpenHolidayDropdownId] = useState<string | null>(null);
  const [holidayToEdit, setHolidayToEdit] = useState<any | null>(null);
  const [holidayToDelete, setHolidayToDelete] = useState<any | null>(null);
  const [deleteHolidayModalOpen, setDeleteHolidayModalOpen] = useState(false);

  const { mutate: createHoliday } = useCreateOfftimeDay();
  const { mutate: updateHoliday } = useUpdateOfftimeDay();
  const { mutate: deleteHoliday } = useDeleteOfftimeDay();

  const formatHolidayLength = (length?: number | string) => {
    if (!length) return '-';
    const hours = Number(length) * 8;
    return `${hours} Std`;
  };

  const [newHolidayForm, setNewHolidayForm] = useState({
    date: '',
    name: '',
    isHalfDay: false,
    isVacation: false,
  });

  const handleEditHoliday = (holiday: any) => {
    setHolidayToEdit(holiday);
    setNewHolidayForm({
      date: holiday.date,
      name: holiday.name,
      isHalfDay: holiday.length === 0.5,
      isVacation: holiday.is_holiday,
    });
    setIsAddHolidayModalOpen(true);
    setOpenHolidayDropdownId(null);
  };

  const handleDeleteHolidayClick = (holiday: any) => {
    setHolidayToDelete(holiday);
    setDeleteHolidayModalOpen(true);
    setOpenHolidayDropdownId(null);
  };

  return (
    <div className="space-y-6">
      {/* Controls Row */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-2 text-[#6b6b6b] text-[14px]">
          <span>Zeige</span>
          <select
            value={holidayItemsPerPage}
            onChange={(e) => {
              setHolidayItemsPerPage(Number(e.target.value));
              setHolidayCurrentPage(1);
            }}
            className="form-select px-2 py-1 border border-[#e0e0e0] rounded text-[#1c1c1c]"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span>Einträge</span>
        </div>

        {/* Add Holiday Button */}
        <Button
          variant="primary"
          onClick={() => setIsAddHolidayModalOpen(true)}
          className="flex items-center gap-2"
        >
          Neuen Feiertag anlegen
        </Button>
      </div>

      {/* Data Display */}
      {holidaysData?.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-[#6b6b6b] text-[16px]">
            Für den ausgewählten Monat sind noch keine Daten vorhanden
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Table Header */}
          <div className="grid grid-cols-4 gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]">
            <div>Datum</div>
            <div>Name</div>
            <div>Dauer</div>
            <div>Ist Urlaub</div>
          </div>

          {/* Table Rows */}
          {holidaysData?.map((entry) => (
            <div
              key={entry.id}
              className="grid grid-cols-4 gap-4 px-4 py-3 text-[16px] items-center hover:bg-[#f5f5f5] transition-colors"
            >
              <div className="text-[#6b6b6b]">{entry.date}</div>
              <div className="text-[#1c1c1c]">{entry.name}</div>
              <div className="text-[#6b6b6b]">{formatHolidayLength(entry.length)}</div>
              <div className="text-[#6b6b6b] flex justify-between items-center">
                <span>{entry.is_holiday ? 'Ja' : 'Nein'}</span>

                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenHolidayDropdownId(
                        openHolidayDropdownId === entry.id ? null : entry.id
                      );
                    }}
                    className="p-1 rounded-[6px] hover:bg-[#f5f5f5] transition-colors"
                  >
                    <MoreVertical className="size-5 text-[#6b6b6b]" />
                  </button>

                  {openHolidayDropdownId === entry.id && (
                    <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[160px]">
                      <button
                        onClick={() => handleEditHoliday(entry)}
                        className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDeleteHolidayClick(entry)}
                        className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                      >
                        Löschen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t border-[#e0e0e0]">
            {/* Info */}
            <div className="text-[#6b6b6b] text-[14px]">
              Zeige{' '}
              {Math.min(
                ((holidaysPaginator?.currentPage ?? 1) - 1) * holidayItemsPerPage + 1,
                holidaysPaginator?.total ?? 0
              )}{' '}
              bis{' '}
              {Math.min(
                (holidaysPaginator?.currentPage ?? 1) * holidayItemsPerPage,
                holidaysPaginator?.total ?? 0
              )}{' '}
              von {holidaysPaginator?.total ?? 0} Einträgen
            </div>

            {/* Page Controls */}
            <div className="flex items-center gap-2">
              {/* Previous */}
              <button
                onClick={() => setHolidayCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={holidaysPaginator?.currentPage === 1}
                className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
              >
                Vorherige
              </button>

              {/* Page Numbers */}
              {(() => {
                const totalPages = holidaysPaginator?.lastPage ?? 1;
                const pages = [];
                const maxVisiblePages = 5;

                let startPage = Math.max(
                  1,
                  (holidaysPaginator?.currentPage ?? 1) - Math.floor(maxVisiblePages / 2)
                );
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                if (endPage - startPage < maxVisiblePages - 1) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }

                return pages.map((page) => (
                  <button
                    key={page}
                    onClick={() => setHolidayCurrentPage(page)}
                    className={`px-3 py-1 text-[14px] rounded ${
                      holidaysPaginator?.currentPage === page
                        ? 'bg-[#0084c7] text-white'
                        : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
                    }`}
                  >
                    {page}
                  </button>
                ));
              })()}

              {/* Next */}
              <button
                onClick={() =>
                  setHolidayCurrentPage((prev) =>
                    Math.min(holidaysPaginator?.lastPage ?? prev, prev + 1)
                  )
                }
                disabled={holidaysPaginator?.currentPage === holidaysPaginator?.lastPage}
                className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
              >
                Nächste
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Holiday Modal */}
      <ConfirmModal
        isOpen={deleteHolidayModalOpen}
        onClose={() => setDeleteHolidayModalOpen(false)}
        title="Feiertag löschen"
        message={
          holidayToDelete
            ? `Möchten Sie den Feiertag "${holidayToDelete.name}" wirklich löschen?`
            : ''
        }
        onConfirm={() => {
          if (holidayToDelete) {
            deleteHoliday({ id: holidayToDelete.id });
          }
          setDeleteHolidayModalOpen(false);
          setHolidayToDelete(null);
        }}
      />

      {/* Add / Edit Holiday Modal */}
      <Modal
        isOpen={isAddHolidayModalOpen}
        onClose={() => {
          setIsAddHolidayModalOpen(false);
          setHolidayToEdit(null);
        }}
        title={holidayToEdit ? 'Feiertag bearbeiten' : 'Feiertag'}
      >
        <div className="space-y-6">
          {/* Two Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Datum */}
            <div className="space-y-2">
              <label className="text-[#6b6b6b] text-[14px]">Datum</label>
              <input
                type="date"
                value={newHolidayForm.date}
                onChange={(e) => setNewHolidayForm({ ...newHolidayForm, date: e.target.value })}
                className="w-full box-border px-4 py-2 border border-[#e0e0e0] rounded-lg text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
              />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <label className="text-[#6b6b6b] text-[14px]">Name</label>
              <input
                type="text"
                placeholder="Name"
                value={newHolidayForm.name}
                onChange={(e) => setNewHolidayForm({ ...newHolidayForm, name: e.target.value })}
                className="w-full box-border px-4 py-2 border border-[#e0e0e0] rounded-lg text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
              />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newHolidayForm.isVacation}
                onChange={(e) =>
                  setNewHolidayForm({ ...newHolidayForm, isVacation: e.target.checked })
                }
                className="size-4 rounded border-[#e0e0e0] text-[#0084c7] focus:ring-2 focus:ring-[#0084c7]"
              />
              <span className="text-[#6b6b6b] text-[14px]">Urlaub</span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="secondary" onClick={() => setIsAddHolidayModalOpen(false)}>
              Schließen
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (!newHolidayForm.date || !newHolidayForm.name) return;

                const payload = {
                  date: newHolidayForm.date,
                  name: newHolidayForm.name,
                  length: newHolidayForm.isHalfDay ? 0.5 : 1,
                  is_holiday: newHolidayForm.isVacation,
                };

                if (holidayToEdit) {
                  updateHoliday({ id: holidayToEdit.id, ...payload });
                } else {
                  createHoliday(payload);
                }

                setIsAddHolidayModalOpen(false);
                setHolidayToEdit(null);
                setNewHolidayForm({
                  date: '',
                  name: '',
                  isHalfDay: false,
                  isVacation: false,
                });
              }}
            >
              Speichern
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}